import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/shared";
import * as Icons from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";

function Stat({ icon, value, label, color }) {
  const I = Icons[icon];
  return (
    <div className="rounded-3xl bg-white p-5 shadow-sm border border-black/5">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl mb-3" style={{ backgroundColor: color + "22" }}><I size={22} color={color} /></div>
      <div className="font-head text-3xl text-csh-blue">{value}</div>
      <div className="text-sm font-semibold text-muted-foreground">{label}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const { data, isLoading } = useQuery({
    queryKey: ["dash", user?.role],
    queryFn: async () => (await api.get(isAdmin ? "/dashboard/admin" : "/dashboard/teacher")).data,
  });
  if (isLoading || !data) return <Loader />;

  if (isAdmin) {
    return (
      <div className="space-y-6" data-testid="admin-dashboard">
        <h2 className="font-head text-3xl text-csh-blue">Program Health 📊</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat icon="Users" value={data.total_students} label="Students" color="#662D91" />
          <Stat icon="GraduationCap" value={data.total_teachers} label="Teachers" color="#FC633B" />
          <Stat icon="Boxes" value={data.total_batches} label="Batches" color="#002957" />
          <Stat icon="CalendarDays" value={data.total_sessions} label="Sessions" color="#22C55E" />
          <Stat icon="CalendarCheck" value={`${data.attendance_rate}%`} label="Attendance" color="#EAB308" />
          <Stat icon="Target" value={`${data.quest_completion}%`} label="Quest Completion" color="#FC633B" />
          <Stat icon="Medal" value={data.total_badges_awarded} label="Badges Awarded" color="#662D91" />
          <Stat icon="Star" value={data.total_points} label="Total Story Points" color="#EAB308" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
            <h3 className="font-head text-xl text-csh-blue mb-4">Story Points by Student</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.student_points}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} tickFormatter={(v) => v.split(" ")[0]} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="points" fill="#662D91" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
            <h3 className="font-head text-xl text-csh-blue mb-4">Badge Distribution</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.badge_distribution.filter((b) => b.count > 0)} dataKey="count" nameKey="name" outerRadius={90} label={(e) => e.name}>
                  {data.badge_distribution.map((b, i) => <Cell key={i} fill={b.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teacher-dashboard">
      <h2 className="font-head text-3xl text-csh-blue">Today's Overview 🎯</h2>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat icon="Users" value={data.total_students} label="My Students" color="#662D91" />
        <Stat icon="CalendarCheck" value={data.present_today} label="Present Today" color="#22C55E" />
        <Stat icon="ClipboardCheck" value={data.pending_reviews} label="Pending Reviews" color="#FC633B" />
        <Stat icon="CalendarDays" value={data.upcoming_sessions?.length || 0} label="Upcoming" color="#002957" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-head text-xl text-csh-blue">Recent Submissions</h3>
            <button onClick={() => navigate("/staff/reviews")} className="text-sm font-bold text-csh-purple">Review all →</button>
          </div>
          <div className="space-y-2">
            {data.recent_submissions.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-xl bg-black/5 px-3 py-2">
                <span className="font-semibold text-csh-blue text-sm">{r.student_name} · {r.quest_name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}`}>{r.status}</span>
              </div>
            ))}
            {data.recent_submissions.length === 0 && <p className="text-sm text-muted-foreground">No submissions yet.</p>}
          </div>
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
          <h3 className="font-head text-xl text-csh-blue mb-3">Upcoming Sessions</h3>
          <div className="space-y-2">
            {data.upcoming_sessions.map((s) => (
              <div key={s.id} className="flex items-center gap-3 rounded-xl bg-black/5 px-3 py-2">
                <Icons.Video size={18} className="text-csh-purple" />
                <div className="text-sm"><b className="text-csh-blue">{s.title}</b><div className="text-xs text-muted-foreground">{s.date} · {s.time}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
